import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/auth.store';
import { captureApiError } from './sentry';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // SEC-05: Send HttpOnly cookies with every request
  timeout: 30000,
});

// Mutex: concurrent 401s share a single refresh call instead of each triggering their own
let refreshPromise: Promise<unknown> | null = null;

// Response interceptor: strip the { success } envelope field, handle 401 + token refresh.
//
// Backend always returns { success: true, data?, meta? } or { success: false, error: {...} }.
// The success handler strips `success` so callers receive { data: T } or { data: T[], meta: {...} }
// directly.  Hooks should NOT chain .then((r) => r.data) — the resolved value IS { data: T }.
// Components access queryResult.data?.data for the item/list, queryResult.data?.meta for pagination.
//
// Error handler rejects with a plain object { message, details?, status } so that
// every catch block can read err.message without inspecting the raw Axios error shape.
api.interceptors.response.use(
  (response) => {
    // Blob responses (PDF downloads, exports) must be returned as-is — do not unwrap.
    // `as any` required: axios's typed interceptor signature can't express our envelope-strip contract.
    if (response.config.responseType === 'blob') return response as any;
    // Strip { success } from the envelope; return the rest ({ data, meta? } or {}).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { success, ...rest } = response.data ?? {};
    // `as any` required: callers downcast to specific response types via generic api.get<T>() / .post<T>().
    return rest as any;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't attempt refresh for auth endpoints — a 401 on login/register
    // means wrong credentials, not an expired session
    const authPaths = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
    const isAuthRequest = authPaths.some((p) => originalRequest.url?.endsWith(p));

    // Check for suspended account BEFORE attempting token refresh.
    // A suspended org should show the overlay immediately, not try to refresh.
    if (error.response?.status === 401) {
      const rawMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        '';
      if (rawMessage.toLowerCase().includes('suspended')) {
        useAuthStore.getState().setSuspended(true);
        // Reject with normalised shape so callers can read err.message
        return Promise.reject({
          message: rawMessage,
          details: undefined,
          status: 401,
        });
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = axios
          .post(
            `${import.meta.env.VITE_API_URL || '/api/v1'}/auth/refresh`,
            {},
            { withCredentials: true },
          )
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        // Cookie-based refresh — HttpOnly cookie sent automatically
        await refreshPromise;
        return api(originalRequest);
      } catch {
        // Only redirect if we're not already on a public/auth page
        const p = window.location.pathname;
        const publicPrefixes = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/resend-verification', '/careers', '/google-return'];
        const isPublic = publicPrefixes.some((prefix) => p.includes(prefix));
        if (!isPublic) {
          window.location.href = '/login';
        }
      }
    }

    // Extract error message + details from the backend envelope.
    // Backend error shape: { success: false, error: { message, statusCode, details?, code? } }
    const apiError = error.response?.data?.error;
    const code: string | undefined = apiError?.code;
    let message: string =
      apiError?.message ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';
    const details: Array<{ field: string; message: string }> | undefined = apiError?.details;
    const status: number = error.response?.status ?? 0;

    // RBAC-aware 403 messaging — map structured codes to human-friendly copy
    if (status === 403 && code) {
      switch (code) {
        case 'PERM_REQUIRED':
          message = 'You do not have permission for this action';
          break;
        case 'MODULE_DISABLED': {
          const mod = (apiError as { module?: string })?.module ?? 'module';
          message = `The ${mod} module is disabled for your organization`;
          break;
        }
        case 'OWNER_IMMUTABLE':
          message = 'The Owner role cannot be modified';
          break;
        case 'OWNER_RESERVED':
          message = 'This permission is reserved for the Owner role';
          break;
        case 'PRIVILEGE_ESCALATION':
          message = 'You cannot grant a permission you do not hold';
          break;
        case 'OWNER_GRANT_DENIED':
          message = 'Only an Owner can grant the Owner role';
          break;
        case 'OWNER_DEMOTE_DENIED':
          message = 'Only an Owner can demote another Owner';
          break;
        case 'LAST_OWNER':
          message = 'Organization must have at least one Owner';
          break;
        case 'PASSWORD_CHANGE_REQUIRED':
          message = 'Please change your temporary password to continue.';
          break;
      }
    }

    // ERR-01: Show toast for non-401 errors (401s are handled above).
    // Probe endpoints that 403 by design for non-super-admins (e.g. /admin/check)
    // stay silent — caller already interprets the rejection as "no access".
    const url: string = error.config?.url ?? '';
    // The must-change-password 403 redirects to a dedicated screen that already
    // shows an explanatory banner — a raw toast on top of it is redundant + ugly.
    const isPwChangeRequired = status === 403 && code === 'PASSWORD_CHANGE_REQUIRED';
    // NOTE: JUSTIFICATION_REQUIRED is deliberately NOT silenced here. Suppressing it
    // globally would mean any future review-submit caller that forgets to map
    // `error.details` (e.g. a management-review form) fails with zero user feedback.
    // The interceptor always toasts; a page that also highlights the offending
    // questions inline simply adds detail on top of the same message.
    // Deleting a recruitment stage that still holds applications opens a "move them to…" dialog
    // carrying this same message, so a toast would only repeat it.
    const isStageInUse = status === 409 && code === 'STAGE_IN_USE';
    const isSilentProbe =
      (status === 403 && /\/admin\/check(\?|$)/.test(url)) || isPwChangeRequired || isStageInUse;
    if (status !== 401 && !isSilentProbe) {
      toast.error(message, { id: `api-error-${status}-${code || ''}` });
    }

    captureApiError({
      message,
      status,
      details,
      url,
      method: (error.config?.method ?? '').toUpperCase(),
    });

    return Promise.reject({ message, details, status, code });
  },
);

export default api;
